import { createFileRoute } from "@tanstack/react-router";
import AnswerPage from "@/pages/AnswerPage";
import { getAnswerPreviewSsr } from "@/lib/answerPreviewSsr.functions";
import {
  answerDescriptionFromBody,
  answerHead,
  answerPath,
  answerSchemaText,
  answersBase,
  resolveAnswerParams,
  sectorLabel,
} from "@/lib/answersSeo";
import { questionText } from "@/data/questionBank";
import { SITE_URL } from "@/lib/site-url";

export const Route = createFileRoute("/consultas-resueltas/$sector/$pregunta")({
  loader: async ({ params }) => { try { return await getAnswerPreviewSsr({ data: { sector: params.sector, question: params.pregunta, lang: "es" } }); } catch { return { content: null }; } },
  head: ({ params, loaderData }) => {
    const resolved = resolveAnswerParams(params.sector, params.pregunta, "es");
    const body = loaderData?.content?.body ?? null;
    const base = resolved
      ? answerHead("es", sectorLabel(resolved.sector, "es"), questionText(resolved.question, "es", resolved.sector.key), resolved.question.category)
      : { title: "Consultas resueltas | Retail Lemon", description: "Respuestas por tipo de negocio." };
    const h = {
      title: base.title,
      description: resolved
        ? answerDescriptionFromBody(body ?? loaderData?.content?.metaDescription, base.description)
        : base.description,
    };
    const pathEs = resolved ? answerPath("es", resolved.sector, resolved.question) : "/consultas-resueltas";
    const pathEn = resolved ? answerPath("en", resolved.sector, resolved.question) : "/en/answers";
    const questionLabel = resolved ? questionText(resolved.question, "es", resolved.sector.key) : "";
    return {
      meta: [
        { title: h.title },
        { name: "description", content: h.description },
        { property: "og:title", content: h.title },
        { property: "og:description", content: h.description },
        { property: "og:url", content: `${SITE_URL}${pathEs}` },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "es_ES" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: h.title },
        { name: "twitter:description", content: h.description },
        ...(resolved ? [] : [{ name: "robots", content: "noindex" }]),
      ],
      links: resolved ? [
        { rel: "canonical", href: `${SITE_URL}${pathEs}` },
        { rel: "alternate", hrefLang: "es", href: `${SITE_URL}${pathEs}` },
        { rel: "alternate", hrefLang: "en", href: `${SITE_URL}${pathEn}` },
        { rel: "alternate", hrefLang: "x-default", href: `${SITE_URL}${pathEs}` },
      ] : [],
      scripts: resolved ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "QAPage",
            inLanguage: "es",
            url: `${SITE_URL}${pathEs}`,
            about: sectorLabel(resolved.sector, "es"),
            mainEntity: {
              "@type": "Question",
              name: questionLabel,
              answerCount: 1,
              acceptedAnswer: {
                "@type": "Answer",
                text: answerSchemaText(body, h.description),
                url: `${SITE_URL}${pathEs}`,
              },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Retail Lemon", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Consultas resueltas", item: `${SITE_URL}${answersBase("es")}` },
              { "@type": "ListItem", position: 3, name: sectorLabel(resolved.sector, "es"), item: `${SITE_URL}${answersBase("es")}?sector=${resolved.sector.key}` },
              { "@type": "ListItem", position: 4, name: questionLabel, item: `${SITE_URL}${pathEs}` },
            ],
          }),
        },
      ] : [],
    };
  },
  component: AnswerRoute,
});

function AnswerRoute() {
  const data = Route.useLoaderData();
  return <AnswerPage routeLang="es" initialContent={data.content} />;
}
